// Application root: wires theme, auth, topology and machine-state providers,
// picks the role-specific view, and handles the public /submit page, the
// login/loading gates and the connection-lost screens.
import React, { useMemo, Suspense, useEffect, lazy } from "react";
import { ColorModeContext, useMode } from "./theme";
import { CssBaseline, ThemeProvider, Box, CircularProgress, Typography } from "@mui/material";
import { Routes, Route, useLocation } from "react-router-dom";
import Topbar from "./scenes/global/Topbar";
import Sidebar from "./scenes/global/Sidebar";
import AdminView from './scenes/AdminView';
import ManagerView from './scenes/ManagerView';
import OperatorView from './scenes/OperatorView';
import AnalystView from './scenes/AnalystView';
import { useAppContext } from './context/AppContext';
import { useAuth } from './auth/AuthContext';
import Login from './components/auth/Login';
import { TopologyProvider } from './hooks/useTopology';
import { MachineStateProvider } from './hooks/useMachineState';
import { ConnectionLostOverlay, ConnectionLostPage } from './components/ConnectionLost';
import { prefetchScenes } from './config/roleRoutes';

// Public customer upload page — reachable without login at /submit.
const SubmitPage = lazy(() => import('./scenes/submit'));

// Fallback while a lazy route chunk loads (split in config/roleRoutes.js).
const RouteFallback = () => (
  <Box display="flex" alignItems="center" justifyContent="center" height="60vh">
    <CircularProgress size={32} sx={{ color: '#1a8a7d' }} />
  </Box>
);

function App() {
  const modeResult = useMode();
  const context = useAppContext();
  const authContext = useAuth();
  const location = useLocation();

  const [theme, colorMode] = modeResult || [null, null];
  const { currentRole = 'admin' } = context || {};
  const {
    isAuthenticated = false,
    loading: authLoading = true,
    serverUnreachableSince = null,
  } = authContext || {};

  // After login, prefetch the other scene chunks at browser idle so later
  // navigations resolve their lazy boundaries from cache.
  useEffect(() => {
    if (!isAuthenticated) return;
    const currentRoute = (location.pathname || '').replace(/^\//, '').split('/')[0];
    const trigger = () => prefetchScenes(currentRoute);
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(trigger, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(id);
    }
    const t = setTimeout(trigger, 1500);
    return () => clearTimeout(t);
  }, [isAuthenticated, location.pathname]);

  const MemoizedRoleView = useMemo(() => {
    console.log('Recalculating role view for:', currentRole);
    
    switch (currentRole) {
      case 'admin':
        return <AdminView />;
      case 'manager':
        return <ManagerView />;
      case 'operator':
        return <OperatorView />;
      case 'analyst':
        return <AnalystView />;
      default:
        return <AdminView />;
    }
  }, [currentRole]);

  if (!theme || !colorMode || !context) {
    return null;
  }

  // Public customer data upload page — no authentication required.
  if (location.pathname === '/submit') {
    return (
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Suspense fallback={<RouteFallback />}>
            <SubmitPage />
          </Suspense>
        </ThemeProvider>
      </ColorModeContext.Provider>
    );
  }

  if (authLoading) {
    return (
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100vh" gap={2}>
            <CircularProgress size={40} sx={{ color: '#1a8a7d' }} />
            <Typography variant="body2" color="text.secondary">Loading...</Typography>
          </Box>
        </ThemeProvider>
      </ColorModeContext.Provider>
    );
  }

  // A stored session the server could not check yet: wait for the server
  // (AuthContext retries) instead of sending the user to the login page.
  if (!isAuthenticated && serverUnreachableSince) {
    return (
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ConnectionLostPage since={serverUnreachableSince} />
        </ThemeProvider>
      </ColorModeContext.Provider>
    );
  }

  if (!isAuthenticated) {
    return (
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Login />
        </ThemeProvider>
      </ColorModeContext.Provider>
    );
  }
  

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <TopologyProvider>
          <MachineStateProvider>
            <div className="app">
              <Sidebar />
              <main className="content">
                <Topbar />
                <Suspense fallback={<RouteFallback />}>
                  {MemoizedRoleView}
                </Suspense>
              </main>
            </div>
            <ConnectionLostOverlay />
          </MachineStateProvider>
        </TopologyProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default React.memo(App);